import { sendSystemNotification, sendPushNotification, buildEmailTemplate } from './notifications';
import { brand as brandConfig } from './branding';

/* ───────────────────────────────────────────────
 * Helper: resolve applicant name + email from app
 * ─────────────────────────────────────────────── */
function resolveRecipient(app: any): { name: string; email: string } {
  const anyApp = app as any;
  const name = `${anyApp.personalDetails?.firstName || ''} ${anyApp.personalDetails?.lastName || ''}`.trim() || anyApp.userEmail || 'Applicant';
  const email = anyApp.userEmail || anyApp.personalDetails?.email || '';
  return { name, email };
}

/** Strip HTML tags for push notification body (plain text only). */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

/** Send a system notification (email + push) — noop if no email resolved. Returns true if email sent. */
async function notify(app: any, subject: string, title: string, bodyHtml: string, notificationType: string, ctaText?: string, ctaUrl?: string): Promise<boolean> {
  const { name, email } = resolveRecipient(app);
  if (!email) return false;

  const frontendUrl = (window as any).__FRONTEND_URL || (import.meta.env as any).VITE_FRONTEND_URL || (typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://portal.isats.org');
  const portalUrl = frontendUrl + '/applicant';

  // Send email notification
  const emailSent = await sendSystemNotification({
    to: email,
    toName: name,
    subject,
    htmlBody: buildEmailTemplate({
      applicantName: name,
      title,
      bodyHtml,
      ctaText: ctaText || 'Log In to Your Portal',
      portalUrl,
      ctaUrl,
    }),
    notificationType,
    applicationId: app.applicationId,
  });

  // Send push notification if the applicant has a userId (FCM tokens registered).
  // The application object consistently uses `userId`, but we fall back to
  // `userUid` / `applicantUserId` defensively for edge cases.
  const uid: string | undefined = app.userId ?? app.userUid ?? app.applicantUserId;
  if (uid) {
    const pushBody = stripHtml(bodyHtml).slice(0, 120);
    sendPushNotification({
      targetUid: uid,
      title: subject,
      body: pushBody || title,
      url: ctaUrl || portalUrl,
    }).catch(() => {});
  }

  return emailSent;
}

/* ───────────────────────────────────────────────
 * TICKETS  (T-1 through T-4)
 * ─────────────────────────────────────────────── */

/** T-1: Ticket created by applicant */
export async function emailTicketCreated(app: any, ticketTitle: string) {
  return notify(
    app,
    `We received your support request - ${ticketTitle}`,
    'Support Request Received',
    `<p>Thank you for reaching out. Your support request has been received and a member of our team will respond shortly.</p>
     <table class="data-table"><tr><td>Request</td><td>${ticketTitle}</td></tr></table>
     <p>You can track the status of your request at any time through your portal.</p>`,
    'ticket_created',
    'View My Requests',
  );
}

/** T-2: Admin posts a public reply (non-internal) */
export async function emailTicketAdminReply(app: any, ticketTitle: string) {
  return notify(
    app,
    `New reply on your ticket: ${ticketTitle}`,
    'New Response on Your Support Request',
    `<p>A new response has been added to your support request:</p>
     <table class="data-table"><tr><td>Request</td><td>${ticketTitle}</td></tr></table>
     <p>Please log in to your portal to view the response and continue the conversation.</p>
     <p style="background:#fef2f2;border-left:3px solid #ef4444;padding:12px;font-size:13px;color:#475569;border-radius:4px;"><strong>Note:</strong> To reply, please log in to the portal. We cannot accept replies to this email.</p>`,
    'ticket_admin_reply',
    'View My Requests',
  );
}

/** T-3: Ticket status changed */
export async function emailTicketStatusChanged(app: any, ticketTitle: string, newStatus: string) {
  return notify(
    app,
    `Ticket Update: ${ticketTitle} is now ${newStatus}`,
    'Support Request Status Update',
    `<p>The status of your support request has been updated:</p>
     <table class="data-table"><tr><td>Request</td><td>${ticketTitle}</td></tr><tr><td>New Status</td><td>${newStatus}</td></tr></table>
     <p>Please log in to your portal for details.</p>`,
    'ticket_status_changed',
    'View My Requests',
  );
}

/** T-4: Ticket closed/resolved */
export async function emailTicketResolved(app: any, ticketTitle: string, resolutionNote?: string) {
  return notify(
    app,
    `Your ticket has been resolved - ${ticketTitle}`,
    'Support Request Resolved',
    `<p>Your support request has been resolved:</p>
     <table class="data-table"><tr><td>Request</td><td>${ticketTitle}</td></tr>${resolutionNote ? `<tr><td>Resolution Note</td><td>${resolutionNote}</td></tr>` : ''}</table>
     <p>If you have any further questions, please feel free to submit a new request through your portal.</p>`,
    'ticket_resolved',
    'View My Requests',
  );
}

/* ───────────────────────────────────────────────
 * APPLICATION STATUS  (A-2 through A-5)
 * ─────────────────────────────────────────────── */

/** A-2: Application returned (sent back for edits) */
export async function emailApplicationReturned(app: any) {
  return notify(
    app,
    'Action Required: Your Application Needs Updates',
    'Application Requires Updates',
    `<p>Your application has been reviewed and requires some updates before it can proceed. Please log in to your portal to review the feedback provided and make the necessary changes.</p>
     <p>Once you have updated the required fields, please resubmit your application for further review.</p>`,
    'application_returned',
    'Review My Application',
  );
}

/** A-3: Application approved */
export async function emailApplicationApproved(app: any) {
  return notify(
    app,
    'Congratulations - Your Application Has Been Approved',
    'Application Approved',
    `<p>Congratulations! Your application has been approved. We are pleased to welcome you to the ${brandConfig.businessName} program.</p>
     <p>Please log in to your portal to review the next steps and any additional information required to proceed with your program placement.</p>`,
    'application_approved',
    'View My Application',
  );
}

/** A-4: Application cancelled */
export async function emailApplicationCancelled(app: any) {
  return notify(
    app,
    `Important Update on Your ${brandConfig.businessName} Application`,
    'Application Status Update',
    `<p>Your application status has been updated. Please log in to your portal for further details regarding this update.</p>
     <p>If you have any questions, please contact your program advisor or submit a support request through your portal.</p>`,
    'application_cancelled',
  );
}

/** A-5: Application deactivated (requires prompt — caller handles confirmation) */
export async function emailApplicationDeactivated(app: any) {
  return notify(
    app,
    'Your Application Has Been Deactivated',
    'Application Deactivated',
    `<p>Your application has been deactivated. This means you will no longer be able to access your application or proceed with the program through this account.</p>
     <p>If you believe this was done in error, or if you would like to reactivate your application, please contact your program advisor.</p>`,
    'application_deactivated',
  );
}

/** A-6: Application archived */
export async function emailApplicationArchived(app: any) {
  return notify(
    app,
    'Your Application Has Been Archived',
    'Application Archived',
    `<p>Your application status has been moved to archived status. Please log in to your portal if you wish to view your account details or contact support.</p>`,
    'application_archived',
  );
}

/* ───────────────────────────────────────────────
 * PLACEMENT  (P-1 through P-3)
 * ─────────────────────────────────────────────── */

/** P-1: Host employer / placement assigned */
export async function emailPlacementAssigned(app: any, hostName?: string) {
  return notify(
    app,
    `Your Host Employer Has Been Assigned - ${brandConfig.businessName}`,
    'Host Employer Assigned',
    `<p>We are pleased to inform you that your host employer has been assigned.</p>
     ${hostName ? `<table class="data-table"><tr><td>Host Employer</td><td>${hostName}</td></tr></table>` : ''}
     <p>Please log in to your portal for full details about your placement, including company information and next steps for your onboarding.</p>`,
    'placement_assigned',
    'View My Placement',
  );
}

/** P-2: Placement start date confirmed */
export async function emailPlacementStartDateConfirmed(app: any, startDate?: string) {
  return notify(
    app,
    'Your Program Start Date is Confirmed',
    'Program Start Date Confirmed',
    `<p>Your program start date has been confirmed.</p>
     ${startDate ? `<table class="data-table"><tr><td>Start Date</td><td>${startDate}</td></tr></table>` : ''}
     <p>Please review the details in your portal and begin preparing for your program. Further instructions regarding travel and accommodation will be provided closer to the date.</p>`,
    'placement_start_date_confirmed',
    'View My Placement',
  );
}

/** P-3: Placement position/role updated */
export async function emailPlacementRoleUpdated(app: any, role?: string) {
  return notify(
    app,
    'Update to Your Placement Details',
    'Placement Details Updated',
    `<p>Your placement details have been updated.</p>
     ${role ? `<table class="data-table"><tr><td>Position / Role</td><td>${role}</td></tr></table>` : ''}
     <p>Please log in to your portal to review the updated information.</p>`,
    'placement_role_updated',
    'View My Placement',
  );
}

/* ───────────────────────────────────────────────
 * VISA & IMMIGRATION  (V-1 through V-5)
 * ─────────────────────────────────────────────── */

/** V-1: DS-2019 number entered */
export async function emailDs2019Ready(app: any) {
  return notify(
    app,
    'Your DS-2019 is Ready - Next Steps Inside',
    'DS-2019 Form Ready',
    `<p>Your DS-2019 form has been prepared and is now available in your portal. This document is required for your visa application.</p>
     <p>Please log in to your portal to review and download the document. Once you have received it, you may proceed with scheduling your visa interview at the nearest U.S. embassy or consulate.</p>`,
    'ds2019_ready',
    'View My Documents',
  );
}

/** V-2: SEVIS ID assigned */
export async function emailSevisIdAssigned(app: any, sevisId?: string) {
  return notify(
    app,
    'Your SEVIS ID Has Been Assigned',
    'SEVIS ID Assigned',
    `<p>Your SEVIS record has been created and your SEVIS ID has been assigned.</p>
     ${sevisId ? `<table class="data-table"><tr><td>SEVIS ID</td><td>${sevisId}</td></tr></table>` : ''}
     <p>This ID is required for your visa application and DS-160 form. Please keep it for your records.</p>`,
    'sevis_id_assigned',
    'View My Visa Details',
  );
}

/** V-3: Visa status changed to Approved */
export async function emailVisaApproved(app: any) {
  return notify(
    app,
    'Great News - Your Visa Has Been Approved',
    'Visa Approved',
    `<p>Congratulations! Your visa application has been approved. You are now cleared to proceed with your program travel.</p>
     <p>Please log in to your portal for details on your visa validity, any conditions attached, and the next steps for your travel preparations.</p>`,
    'visa_approved',
    'View My Visa Details',
  );
}

/** V-4: Visa status changed to Denied */
export async function emailVisaDenied(app: any) {
  return notify(
    app,
    'Visa Update - Please Contact Your Advisor',
    'Visa Application Update',
    `<p>We regret to inform you that your visa application has not been approved at this time. A member of our team will reach out to discuss your options and next steps.</p>
     <p>In the meantime, please contact your program advisor for guidance on alternative pathways or reapplication procedures.</p>`,
    'visa_denied',
    'Contact My Advisor',
  );
}

/** V-5: Embassy interview scheduled (via Events) */
export async function emailEmbassyInterviewScheduled(app: any, eventTitle: string, eventDate?: string) {
  return notify(
    app,
    'Your Embassy Interview is Scheduled',
    'Embassy Interview Scheduled',
    `<p>An embassy interview has been scheduled as part of your visa process.</p>
     <table class="data-table"><tr><td>Event</td><td>${eventTitle}</td></tr>${eventDate ? `<tr><td>Date</td><td>${eventDate}</td></tr>` : ''}</table>
     <p>Please log in to your portal for the full event details, including time, location, and any documents you need to bring.</p>`,
    'embassy_interview_scheduled',
    'View My Events',
  );
}

/* ───────────────────────────────────────────────
 * DOCUMENTS  (D-1 through D-4)
 * ─────────────────────────────────────────────── */

/** D-1: New required document added by admin (via documentRequired field) */
export async function emailNewRequiredDocument(app: any, docName: string) {
  return notify(
    app,
    'Action Required: New Document Needed',
    'New Document Required',
    `<p>A new document has been added to your list of required documents:</p>
     <table class="data-table"><tr><td>Document</td><td>${docName}</td></tr></table>
     <p>Please upload this document through your portal at your earliest convenience to avoid any delays in your application processing.</p>`,
    'new_required_document',
    'Upload Document',
  );
}

/** D-2: Document approved by admin */
export async function emailDocumentApproved(app: any, docType: string) {
  return notify(
    app,
    'Your Document Has Been Approved',
    'Document Approved',
    `<p>Your document has been reviewed and approved.</p>
     <table class="data-table"><tr><td>Document</td><td>${docType}</td></tr></table>
     <p>No further action is required for this document at this time.</p>
     <p style="font-size: 11px; color: #64748b; margin-top: 16px; border-top: 1px solid #e2e8f0; padding-top: 8px; font-style: italic;">Disclaimer: Our admissions and compliance team routinely audits approved and rejected documents. System decisions may be re-evaluated during manual review if additional verification or clarification is required.</p>`,
    'document_approved',
    'View My Documents',
  );
}

/** D-3: Document rejected by admin */
export async function emailDocumentRejected(app: any, docType: string, reason?: string) {
  return notify(
    app,
    'Document Not Accepted - Please Re-upload',
    'Document Requires Re-upload',
    `<p>Your document could not be accepted. Please review the reason below and upload a corrected version.</p>
     <table class="data-table"><tr><td>Document</td><td>${docType}</td></tr>${reason ? `<tr><td>Reason</td><td>${reason}</td></tr>` : ''}</table>
     <p>Please upload a corrected document through your portal at your earliest convenience.</p>
     <p style="font-size: 11px; color: #64748b; margin-top: 16px; border-top: 1px solid #e2e8f0; padding-top: 8px; font-style: italic;">Disclaimer: Our admissions and compliance team routinely audits approved and rejected documents. System decisions may be re-evaluated during manual review if additional verification or clarification is required.</p>`,
    'document_rejected',
    'Re-upload Document',
  );
}

/** D-4: Program package uploaded */
export async function emailProgramPackageReady(app: any, packageName: string) {
  return notify(
    app,
    'Your Program Package is Ready',
    'Program Package Ready',
    `<p>Your program package is now available for download:</p>
     <table class="data-table"><tr><td>Package</td><td>${packageName}</td></tr></table>
     <p>Please log in to your portal to download your program documents and review the contents carefully.</p>`,
    'program_package_ready',
    'Download Package',
  );
}

/* ───────────────────────────────────────────────
 * SIGNING  (S-1 through S-2)
 * ─────────────────────────────────────────────── */

/** S-1: Signing request sent to applicant (the existing reminder covers this) — already done via email tab */

/** S-2: Document fully signed (all parties completed) */
export async function emailDocumentFullySigned(app: any, templateName: string) {
  return notify(
    app,
    'Your Document Has Been Fully Signed',
    'Document Fully Signed',
    `<p>All required parties have signed the document.</p>
     <table class="data-table"><tr><td>Document</td><td>${templateName}</td></tr></table>
     <p>The fully executed document is now available for download in your portal.</p>`,
    'document_fully_signed',
    'View Signed Documents',
  );
}

/* ───────────────────────────────────────────────
 * FINANCE  (F-1 through F-4)
 * ─────────────────────────────────────────────── */

/** F-1: New invoice issued (upgrade from prompt to auto) */
export async function emailNewInvoice(app: any, invoiceNumber: string, amount: string, currency: string, dueDate?: string) {
  return notify(
    app,
    `New Invoice - #${invoiceNumber}`,
    'New Invoice Added',
    `<p>A new invoice has been added to your account:</p>
     <table class="data-table">
       <tr><td>Invoice Number</td><td>#${invoiceNumber}</td></tr>
       <tr><td>Amount</td><td>${currency} ${amount}</td></tr>
       ${dueDate ? `<tr><td>Due Date</td><td>${dueDate}</td></tr>` : ''}
     </table>
     <p>Please review and process the payment through your portal at your earliest convenience.</p>`,
    'invoice_added',
    'View My Invoices',
  );
}

/** F-2: Invoice marked Paid/Approved */
export async function emailInvoicePaid(app: any, invoiceNumber: string, amount: string, currency: string) {
  return notify(
    app,
    `Payment Confirmed - Invoice #${invoiceNumber}`,
    'Invoice Payment Confirmed',
    `<p>Your payment has been confirmed for the following invoice:</p>
     <table class="data-table">
       <tr><td>Invoice Number</td><td>#${invoiceNumber}</td></tr>
       <tr><td>Amount Paid</td><td>${currency} ${amount}</td></tr>
       <tr><td>Status</td><td>Paid</td></tr>
     </table>
     <p>Thank you for your payment. Your account is now up to date.</p>`,
    'invoice_paid',
    'View My Invoices',
  );
}

/** F-3: Receipt approved */
export async function emailReceiptApproved(app: any) {
  return notify(
    app,
    'Your Payment Receipt Has Been Approved',
    'Payment Receipt Approved',
    `<p>Your submitted payment receipt has been reviewed and approved. It has been recorded against your account.</p>
     <p>No further action is required. You can view the approved receipt in your portal at any time.</p>`,
    'receipt_approved',
    'View My Receipts',
  );
}

/** F-4: Receipt rejected */
export async function emailReceiptRejected(app: any, reason?: string) {
  return notify(
    app,
    'Receipt Issue - Please Review',
    'Receipt Not Accepted',
    `<p>Your submitted receipt could not be accepted.</p>
     ${reason ? `<p><strong>Reason:</strong> ${reason}</p>` : ''}
     <p>Please review and upload a corrected receipt through your portal at your earliest convenience.</p>`,
    'receipt_rejected',
    'Re-upload Receipt',
  );
}

/* ───────────────────────────────────────────────
 * EVENTS  (E-1 through E-3)
 * ─────────────────────────────────────────────── */

/** E-1: Event added for applicant */
export async function emailEventScheduled(app: any, eventTitle: string) {
  return notify(
    app,
    `New Event Scheduled: ${eventTitle}`,
    'New Event Scheduled',
    `<p>A new event has been scheduled for you:</p>
     <table class="data-table"><tr><td>Event</td><td>${eventTitle}</td></tr></table>
     <p>Please log in to your portal for full details, including date, time, location, and any preparation required.</p>`,
    'event_scheduled',
    'View My Events',
  );
}

/** E-2: Event updated (date/location changed) */
export async function emailEventUpdated(app: any, eventTitle: string) {
  return notify(
    app,
    `Event Update: ${eventTitle}`,
    'Event Details Updated',
    `<p>The details for your scheduled event have been updated:</p>
     <table class="data-table"><tr><td>Event</td><td>${eventTitle}</td></tr></table>
     <p>Please log in to your portal to review the updated information.</p>`,
    'event_updated',
    'View My Events',
  );
}

/* ───────────────────────────────────────────────
 * SPONSOR / PROGRAM  (SP-1 through SP-3)
 * ─────────────────────────────────────────────── */

/** SP-1: Sponsor / service provider assigned */
export async function emailSponsorAssigned(app: any, sponsorName?: string) {
  return notify(
    app,
    `Your Sponsor Has Been Assigned - ${brandConfig.businessName}`,
    'Sponsor Assigned',
    `<p>We are pleased to inform you that a sponsor has been assigned to oversee your program.</p>
     ${sponsorName ? `<table class="data-table"><tr><td>Sponsor</td><td>${sponsorName}</td></tr></table>` : ''}
     <p>Your sponsor will provide guidance and support throughout your program. Please log in to your portal for contact information.</p>`,
    'sponsor_assigned',
    'View My Sponsor',
  );
}

/** SP-2: Program type changed by admin */
export async function emailProgramChanged(app: any, oldProgram?: string, newProgram?: string) {
  return notify(
    app,
    'Update to Your Program Assignment',
    'Program Assignment Updated',
    `<p>Your program assignment has been updated.</p>
     <table class="data-table">
       ${oldProgram ? `<tr><td>Previous Program</td><td>${oldProgram}</td></tr>` : ''}
       <tr><td>Current Program</td><td>${newProgram || 'Updated'}</td></tr>
     </table>
     <p>Please log in to your portal to review the updated program details and any changes to your required documents.</p>`,
    'program_changed',
    'View My Program',
  );
}

/** SC-1: Scheduling invite sent to applicant (Invite for Event) */
export async function emailSchedulingInvite(
  app: any,
  eventTitle: string,
  schedulingUrl: string,
  agentName: string,
  description?: string
) {
  const descHtml = description ? `<p>${description}</p>` : '';
  return notify(
    app,
    `You're Invited to Schedule: ${eventTitle}`,
    'Schedule Your Meeting',
    `<p>${agentName} has invited you to schedule a meeting for:</p>
     <p style="font-weight:700;margin:12px 0;">${eventTitle}</p>
     ${descHtml}
     <p>Click the button below to view available time slots and select a date that works for you.</p>`,
    'scheduling_invite',
    'Select Date & Time',
    schedulingUrl,
  );
}

/** SP-3: Intended arrival date set */
export async function emailArrivalDateSet(app: any, arrivalDate?: string) {
  return notify(
    app,
    'Your Arrival Date Has Been Set',
    'Arrival Date Confirmed',
    `<p>Your intended arrival date has been recorded.</p>
     ${arrivalDate ? `<table class="data-table"><tr><td>Arrival Date</td><td>${arrivalDate}</td></tr></table>` : ''}
     <p>Please ensure your travel arrangements are in order. Further instructions regarding check-in and orientation will be provided closer to the date.</p>`,
    'arrival_date_set',
    'View My Travel Details',
  );
}
